<template>
  <div class="app-container" ref="infoBox" :class="{'start': page > 0}">
    <!-- Logo -->
    <div class="text-center mb-5 mt-3" v-if="page >= 0">
      <img alt="Winefamly" src="@/assets/logo.svg" />
    </div>

    <!-- Intro headline -->
    <transition name="fade">
      <div class="mt-4 text-center" v-show="page == 0">
        <h1 class="intro-headline">Find dit vin-match</h1>
        <h4 class="intro-subhead">Tager kun 2 minutter</h4>
      </div>
    </transition>

    <!-- Progress bar -->
    <div class="my-3 my-sm-5 steps" v-if="page >= 0">
      <span class="float-left cursor-pointer back-icon" @click="backOnePage" v-if="page > 0">
        <font-awesome-icon icon="reply" size="lg" />
      </span>
      <ul v-bind:class="stepProgress">
        <li
          :class="{'active': page == index, 'completed': page > index}"
          v-for="(question, index) in questions"
          :key="index"
        >{{ index + 1 }}</li>
      </ul>
    </div>

    <!-- Last page of the quiz -->
    <div class="mt-4 text-center" v-if="page == questions.length">
      <h3>Vi er ved at samle dine svar</h3>

      <h1 class="mt-5" v-if="!submitSuccessful">
        <font-awesome-icon icon="spinner" size="lg" class="fa-spin" />
      </h1>

      <div class="mt-5" v-if="submitSuccessful">
        <div>
          <img src="https://cdn.winefamly.com/images/quiz/svg/gnome.svg" />
        </div>

        <b-button class="mt-3" variant="danger" @click="seeResults">Se resultatet</b-button>
      </div>
    </div>

    <!-- Pages with answers -->
    <div class="mt-4 text-center" v-for="(question, index) in questions" :key="index">
      <div v-if="page == index">
        <h4>{{ question.text }}</h4>
        <p v-if="question.answerType == 'multiple'" v-translate>You can pick as many as you like</p>

        <div class="mt-5 mb-5">
          <div
            class="card-container ml-sm-2 mr-sm-2 mb-2 mt-2"
            v-for="(answer, answerIndex) in question.answers"
            :key="answerIndex"
            :class="{
              'mobile-rectangle': question.answers.length < 4 && question.answerType != 'counter', 
              'mobile-square': question.answers.length >= 4 && question.answerType != 'counter', 
              'mobile-counter': question.answerType == 'counter', 
              'show-card': loaded,
              'prices': question.text === 'Hvor meget synes du at en god flaske hverdagsvin bør koste?'
              }"
          >
            <div
              class="card"
              :class="{'selected': answer.selected, 'counter-card': question.answerType == 'counter', 'cursor-pointer': question.answerType != 'counter'}"
              @click="handleAnswer(answer, question, index)"
            >
              <div class="card-body">
                <div v-if="question.answerType == 'multiple'" class="multiselect"></div>
                <div class="image-holder">
                  <img alt="Answer" :src="answer.imageUrl" />
                </div>

                <h5 v-if="question.text !== 'Hvor meget synes du at en god flaske hverdagsvin bør koste?'" class="answer-text">{{ answer.text }}</h5>
              </div>
            </div>

            <br />

            <div class="counter" v-if="question.answerType == 'counter'">
              <font-awesome-icon
                icon="minus"
                class="cursor-pointer"
                @click="reduceCounter($event, answer, question, index, answerIndex)"
              />
              <input type="text" class="counter-input" disabled v-model="answer.counter" />
              <font-awesome-icon
                icon="plus"
                class="cursor-pointer"
                @click="increaseCounter($event, answer, question, index, answerIndex)"
              />
            </div>
          </div>

          <div class="mt-lg-5" v-if="question.answerType == 'counter'">
            <span>{{ question.allCounters }}</span>
            /
            <span>{{ question.minimumAnswersRequired }}</span>
            valgt
          </div>
        </div>

        <div class="mt-2" :class="{'actions-fixed': actionsFixed}" v-if="question.showNextButton">
          <b-button variant="danger" @click="forwardOnePage">Næste spørgsmål ></b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Import config

// Import our custom Http request instance (using Axios)
import winefamlyHttp from "@/util/configureAxios.js";

export default {
  name: "Quiz",
  data: function() {
    return {
      showError: false,
      errorMessage: "",
      page: 0,
      quizTitle: "",
      questions: [],
      quizId: "",
      answers: [],
      submitSuccessful: false,
      responseID: "",
      loaded: true,
      actionsFixed: false,
      skipQuestion: false
    };
  },
  props: {},
  mounted: function() {
    this.$translate.setLang("da_DK");
  },
  computed: {
    stepProgress() {
      return "step-" + this.page;
    }
  },
  methods: {
    // Load data from the API method declaration
    loadData() {
      // keep reference to this object to use it in asynchronous functions
      let self = this;

      // Make GET request to the API to get the data
      winefamlyHttp.get("sommelier/quiz").then(
        function(response) {
          self.quizTitle = response.data.title;
          self.questions = response.data.questions;
          self.quizId = response.data.id;

          self.getUrlParameters();
        },
        function(error) {
          if (error) {
            self.showError = true;
            self.errorMessage = error;
          }
        }
      );
    },
    // Start quiz function (go to first question)
    startQuiz() {
      let self = this;

      this.loaded = false;
      window.dataLayerHD.push({
        event: "QuizStep",
        eventCategory: "Quiz",
        eventAction: "Saw Step",
        eventLabel: "QuizPage2"
      });

      setTimeout(function() {
        self.loaded = true;
      }, 300);

      this.page = 1;

      // On quiz start, check for counter question type
      // and set all answers counters on 0
      this.questions.forEach(function(question) {
        if (question.answerType == "counter") {
          question.allCounters = 0;
          question.answers.forEach(function(answer) {
            answer.counter = 0;
          });
        }
      });
    },
    // Handle answer function (called on answer click)
    handleAnswer(answer, question, index) {
      let self = this;
      let answers = question.answers;

      if (question.answerType == "multiple") {
        // If question requires multiple answers

        let numberOfSelected = 0;
        if (answer.selected) {
          answer.selected = false;
        } else {
          answer.selected = true;
        }

        this.$set(this.questions, index, question);

        // Count number of selected answers
        answers.forEach(function(item) {
          if (item.selected) {
            numberOfSelected += 1;
          }
        });

        // Show next question button only if satisfies requirements from the API
        if (
          numberOfSelected >= question.minimumAnswersRequired &&
          numberOfSelected <= question.maximumAnswersAllowed
        ) {
          question.showNextButton = true;
          this.$set(this.questions, index, question);
        } else {
          question.showNextButton = false;
          this.$set(this.questions, index, question);
        }

        self.setButtonFixed();
      } else if (question.answerType == "counter") {
        // If question requires multiple answers with number of items
      } else {
        // If question requires only one answer
        answers.forEach(function(item) {
          item.selected = false;
        });
        answer.selected = true;

        setTimeout(function() {
          self.forwardOnePage();
        }, 200);
      }
    },
    // Function for handling click on minus counter
    reduceCounter(evt, answer, question, index, answerIndex) {
      evt.stopPropagation();
      if (answer.counter > 0) {
        answer.counter -= 1;
        question.allCounters -= 1;

        if (question.allCounters >= question.minimumAnswersRequired) {
          question.showNextButton = true;
        } else {
          question.showNextButton = false;
        }
        this.$set(question.answers, answerIndex, answer);
        this.$set(this.questions, index, question);

        this.setButtonFixed();
      }
    },
    // Function for handling click on plus counter
    increaseCounter(evt, answer, question, index, answerIndex) {
      evt.stopPropagation();
      if (question.allCounters < question.maximumAnswersAllowed) {
        answer.counter += 1;
        question.allCounters += 1;

        if (question.allCounters >= question.minimumAnswersRequired) {
          question.showNextButton = true;
        } else {
          question.showNextButton = false;
        }

        this.$set(question.answers, answerIndex, answer);
        this.$set(this.questions, index, question);

        this.setButtonFixed();
      }
    },
    // Previous question (reduce page by 1)
    backOnePage() {
      let self = this;
      this.page -= 1;

      setTimeout(function() {
        self.setButtonFixed();
      }, 500);
    },
    // Previous question (reduce page by 1)
    forwardOnePage() {
      let self = this;

      this.loaded = false;

      setTimeout(function() {
        self.loaded = true;
        window.scrollTo(0, 0);
        self.setButtonFixed();
      }, 300);

      let selectedAnswers = [];
      this.page == this.skipQuestion - 1 ? (this.page += 2) : (this.page += 1);

      window.dataLayerHD.push({
        event: "QuizStep",
        eventCategory: "Quiz",
        eventAction: "Saw Step",
        eventLabel: "QuizPage" + (this.page + 1)
      });

      // If current page is last number of questions
      if (this.page == this.questions.length) {
        self.answers = [];

        this.questions.forEach(function(question) {
          selectedAnswers = [];
          question.answers.forEach(function(answer) {
            if (answer.selected) {
              selectedAnswers.push(answer.id);
            }
            if (answer.counter) {
              for (let i = 0; i < answer.counter; i++) {
                selectedAnswers.push(answer.id);
              }
            }
          });

          self.answers.push({
            questionId: question.id,
            answerIds: selectedAnswers
          });
        });

        // Post answers to the API
        this.postAnswers();
      }
    },
    postAnswers() {
      // keep reference to this object to use it in asynchronous functions
      let self = this;

      // Make GET request to the API to get the data
      winefamlyHttp
        .post("sommelier/quiz", {
          quizId: this.quizId,
          answers: this.answers
        })
        .then(
          function(response) {
            if (response.data) {
              setTimeout(function() {
                self.submitSuccessful = true;
                self.responseID = response.data;
              }, 300);
            }
          },
          function(error) {
            if (error) {
              self.showError = true;
              self.errorMessage = error;
            }
          }
        );
    },
    seeResults() {
      // Function that redirects to the shop page with param received from the API
      let url =
        "https://www.winefamly.com/quiz/result/?quizid=" + this.responseID;
      window.location.href = url;
    },
    setButtonFixed() {
      // Function that checks whether the content exceeds window height, and sets fixed positon for next buttons
      let self = this,
        appHeight = this.$refs.infoBox.clientHeight;
      if (appHeight >= window.innerHeight) {
        self.actionsFixed = true;
      } else {
        self.actionsFixed = false;
      }
    },
    getUrlParameters() {
      const url = window.location.href;
      let queryStart = url.indexOf("?") + 1,
        queryEnd = url.indexOf("#") + 1 || url.length + 1,
        query = url.slice(queryStart, queryEnd - 1),
        pairs = query.replace(/\+/g, " ").split("&"),
        parms = {},
        i,
        n,
        v,
        nv,
        self = this,
        questionNum = 0;

      if (query === url || query === "") return;

      for (i = 0; i < pairs.length; i++) {
        nv = pairs[i].split("=", 2);
        n = decodeURIComponent(nv[0]);
        v = decodeURIComponent(nv[1]);

        if (!parms.hasOwnProperty(n)) parms[n] = [];
        parms[n].push(nv.length === 2 ? v : null);
      }

      if (Object.keys(parms).length !== 0) {
        let selectedAnswers = [];

        self.questions.forEach(function(question) {
          selectedAnswers = [];

          // Set question to skip
          question.id === parms.qid[0]
            ? (self.skipQuestion = questionNum)
            : questionNum++;

          question.answers.forEach(function(answer) {
            if (answer.id === parms.aid[0]) {
              answer.selected = true;
              selectedAnswers.push(answer.id);
            }
          });

          self.answers.push({
            questionId: parms.qid[0],
            answerIds: selectedAnswers
          });
        });

        if (this.page === this.skipQuestion) {
          window.dataLayerHD.push({
            event: "QuizStep",
            eventCategory: "Quiz",
            eventAction: "Saw Step",
            eventLabel: "QuizPage" + (this.page + 1)
          });
          this.page += 1;
        }
      }
    }
  },
  created() {
    this.loadData();

    // On quiz start, check for counter question type
    // and set all answers counters on 0
    this.questions.forEach(function(question) {
      if (question.answerType == "counter") {
        question.allCounters = 0;
        question.answers.forEach(function(answer) {
          answer.counter = 0;
        });
      }
    });

    window.dataLayerHD.push({
      event: "QuizStep",
      eventCategory: "Quiz",
      eventAction: "Saw Step",
      eventLabel: "QuizPage1"
    });
  }
};
</script>
